// Inspecteur → Bedreigingen (threat register) — FRONTEND-BLUEPRINT §3.1 / §4. Re-skin on the shadcn design
// system: PageHeader + DataTable, reusing api.listBedreigingen()/createBedreiging() (no new endpoints).
// Columns: category · class · name · description. Threats feed Asset×Threat risk in the Risico register.
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import type { Column } from "@/components/data-table";
import {
  RegisterScreen, CreateDialog, EditDialog, DeactivateButton, TagBadge, truncate, type Row, type FormField,
} from "./_shared";
import { THREAT_CATEGORIES, THREAT_CLASSES, PROBABILITY_6 } from "../../lib/taxonomy";

export function InspectorThreatsRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["bedreigingen"] });
  const [editing, setEditing] = useState<string | null>(null);

  // Catalog-backed dropdowns (LEGACY-AUDIT T2/T3); labels via i18n (P3). Built in-component so t() is in scope.
  const FIELDS: FormField[] = [
    { key: "name", label: t("ins.threats.field.name"), required: true },
    { key: "category", label: t("ins.threats.field.category"), type: "select", options: THREAT_CATEGORIES },
    { key: "threatClass", label: t("ins.threats.field.class"), type: "select", options: THREAT_CLASSES },
    { key: "probability", label: t("ins.threats.field.probability"), type: "select", options: PROBABILITY_6 },
    { key: "description", label: t("ins.threats.field.description"), type: "textarea" },
  ];

  const columns: Column<Row>[] = [
    { key: "name", header: t("ins.threats.col.name"), render: (r) => <span className="font-medium">{String(r.name ?? "—")}</span> },
    { key: "threatClass", header: t("ins.threats.col.class"), render: (r) => <TagBadge value={r.threatClass} /> },
    { key: "category", header: t("ins.threats.col.category"), render: (r) => <TagBadge value={r.category} /> },
    { key: "probability", header: t("ins.threats.col.probability"), render: (r) => <TagBadge value={r.probability} /> },
  ];

  return (
    <>
      <RegisterScreen
        title={t("nav.threats")}
        description={t("inspector.threats.sub")}
        help={t("help.threats")}
        queryKey="bedreigingen"
        listFn={() => api.listBedreigingen()}
        columns={columns}
        empty={t("inspector.threats.empty")}
        readPerm="bedreiging.read"
        onRowClick={(r) => { if (r.id) void navigate({ to: "/bedreigingen/$id", params: { id: String(r.id) } }); }}
        actions={
          <Can perm="bedreiging.write">
            <CreateDialog
              title={t("inspector.threats.new")}
              fields={FIELDS}
              onCreate={(i) => api.createBedreiging(i as unknown as Parameters<typeof api.createBedreiging>[0])}
              onDone={invalidate}
            />
          </Can>
        }
        rowActions={(r) => {
          const id = String(r.id);
          return (
            <span className="inline-flex items-center gap-1">
              <Can perm="bedreiging.update"><button type="button" className="text-xs text-primary hover:underline" onClick={() => setEditing(id)}>{t("crud.edit")}</button></Can>
              <Can perm="bedreiging.delete"><DeactivateButton basePath={TENANT_PATHS.bedreigingen} id={id} onDone={invalidate} /></Can>
            </span>
          );
        }}
      />
      {editing && (
        <EditDialog
          basePath={TENANT_PATHS.bedreigingen}
          id={editing}
          title={t("crud.edit")}
          fields={FIELDS}
          onClose={() => setEditing(null)}
          onDone={() => { invalidate(); setEditing(null); }}
        />
      )}
    </>
  );
}
